// A repeat connect between two people who already share a room — the writes each
// phone makes once the host's verdict is known (meetingDecision; the scanner adopts it
// from the host's reply). One implementation for both sides of a profile-to-profile
// connect (was two parallel ~30-line copies in qrPXPExchange — refactor row 2026-07-18).
//
//   reconnect → the same meeting: refresh what the other person shares (a union —
//               defaults never revoke grants), nothing else. The record, its place and
//               its confirmed venue stand; no banner, no meeting row.
//   re-meet   → a new meeting: a new record (start time, key, this phone's place, the
//               venue unconfirmed again), its own banner and meeting row, and the late
//               location fill when the fix wasn't ready at the exchange instant.
//
// Permanent data layer: no React — `*Ref` arguments are plain { current } holders.

import { hasVenueOrAddress, formatLocationForDisplay } from '../location';
import { persistMessage } from '../chat/messageStore';
import { buildSystemMeetingMessage } from '../chat/messageBuilders';
import { formatSystemMessageLocation } from '../../utils/locationFormatters';
import {
  saveConnectionMetadata,
  getReceivedAnonData,
  getConnectionMetadata,
  upsertConnectionView,
} from '../storage/userDataStorage';
import { fillMeetingLocationIfEmpty } from './meetingRows';
import { MEETING_KIND } from './meetingRule';

export const getSystemMessageId = (chatRoomId, isoTimestamp, userId) => {
  const compact = isoTimestamp.replace(/\D/g, '').slice(0, 14);
  return `system_${chatRoomId}_${compact}_${userId}`;
};

const mergeAnonDataForReconnection = async (chatRoomId, freshProfileData, updateAnonData) => {
  // PXP is Profile-to-Profile — the "other person" data is stored under received_anon_data_
  const existing = (await getReceivedAnonData(chatRoomId)) || {};
  const existingShared = existing.sharedFields || [];
  const freshShared = freshProfileData.sharedFields || [];
  const mergedSharedFields = [...new Set([...existingShared, ...freshShared])];

  const merged = {
    ...existing,
    ...freshProfileData,
    sharedFields: mergedSharedFields,
  };
  // Never downgrade a granted name form (user ruling 2026-07-18): the re-meet
  // payload declares the DEFAULT share (nameSharing 'first'), not a revocation —
  // spreading it over a full-name grant reverted "Emma patterson" → "Emma"
  // (prod field evidence, 2026-07-16). Same principle as the sharedFields
  // union above: defaults refresh values, never revoke grants. The default
  // payload only carries the first name, so the granted full name is kept.
  if (existing.nameSharing === 'full' && freshProfileData.nameSharing === 'first') {
    merged.nameSharing = 'full';
    if (existing.name) merged.name = existing.name;
  }
  await updateAnonData(chatRoomId, merged);
};

const startNewMeetingRecord = async (chatRoomId, newMeetingMetadata, updateConnectionMetadata) => {
  const existing = (await getConnectionMetadata(chatRoomId)) || {};
  const merged = {
    ...existing,
    ...newMeetingMetadata,
    venueConfirmed: false, // New meeting, no venue confirmed yet
  };
  // New meeting: never inherit the previous meeting's venue. The venue lookup
  // usually hasn't resolved at the exchange instant — without this, the old
  // venue survived under the new meetingDate (defect 2). The late-location
  // backfill fills these when the fix arrives; otherwise the meeting is time-only.
  if (!newMeetingMetadata.locationData) delete merged.locationData;
  if (!newMeetingMetadata.location) delete merged.location;
  await saveConnectionMetadata(chatRoomId, merged);
  await updateConnectionMetadata(chatRoomId, merged);
  return merged;
};

// A re-meet's record is a locationDataRef snapshot at the exchange instant — the venue
// lookup usually hasn't resolved yet. Await the pending fix, re-persist own record +
// banner (same id → upsert), fill the meeting row. No peer push — each side keeps its
// own place.
const backfillNewMeetingLocation = ({
  chatRoomId,
  profileUserId,
  record,
  bannerId,
  locationDataRef,
  locationPromiseRef,
  updateConnectionMetadata,
  onRecord,
}) => {
  (async () => {
    try {
      let locationData = null;
      if (locationPromiseRef?.current) {
        try {
          locationData = await locationPromiseRef.current;
        } catch (err) {
          console.log('[REPEAT_CONNECT] backfill: location promise failed:', err);
        }
      }
      if (!locationData) {
        console.log('[REPEAT_CONNECT] backfill: no location fix — meeting stays time-only');
        return;
      }
      locationDataRef.current = locationData;

      const updated = { ...record, locationData, lastUpdated: new Date().toISOString() };
      if (hasVenueOrAddress(locationData)) {
        updated.location = formatLocationForDisplay(locationData);
      }
      await saveConnectionMetadata(chatRoomId, updated);
      await updateConnectionMetadata(chatRoomId, updated);

      // Re-persist own banner so the persisted copy picks up the late place
      // (system is in persistMessage's overwrite list — same id updates the row)
      await persistMessage(
        chatRoomId,
        buildSystemMeetingMessage({
          id: bannerId,
          content: formatSystemMessageLocation(updated),
          meetingDate: updated.meetingDate,
          meetingUtcOffset: updated.meetingUtcOffset,
        })
      );
      // The meeting row takes the area now and the venue only once this side picks
      // it (gated inside — the system suggests, never picks)
      fillMeetingLocationIfEmpty(chatRoomId, profileUserId, updated);
      if (onRecord) await onRecord(updated);
    } catch (err) {
      console.error('[REPEAT_CONNECT] backfill error:', err);
    }
  })();
};

/**
 * @param {Object} p
 * @param {'reconnect'|'remeet'} p.kind - the host's verdict
 * @param {string} p.chatRoomId - the room the two already share
 * @param {string} p.profileUserId - this phone's profile id
 * @param {Object|null} p.freshProfileData - what the other person's handshake carried
 * @param {Object} p.meetingMetadata - this phone's record for a NEW meeting (re-meet):
 *   meetingDate/offset/key (the host's), via, exchangeType, and this phone's place
 * @param {Function} p.updateAnonData
 * @param {Function} p.updateConnectionMetadata
 * @param {{current}} p.locationDataRef
 * @param {{current}} p.locationPromiseRef
 * @param {Function} [p.onRecord] - called with the new meeting's record, and again once
 *   its late place lands (the guest flow's host passes it on — the guest's place
 *   comes from the host)
 */
export const applyRepeatConnect = async ({
  kind,
  chatRoomId,
  profileUserId,
  freshProfileData,
  meetingMetadata,
  updateAnonData,
  updateConnectionMetadata,
  locationDataRef,
  locationPromiseRef,
  onRecord,
}) => {
  if (freshProfileData) {
    await mergeAnonDataForReconnection(chatRoomId, freshProfileData, updateAnonData);
  }
  if (kind !== MEETING_KIND.REMEET) return; // a reconnect records nothing new

  const record = await startNewMeetingRecord(chatRoomId, meetingMetadata, updateConnectionMetadata);
  upsertConnectionView(chatRoomId, profileUserId, { metadata: meetingMetadata });
  // Time-first: persist the re-meet banner even before the place resolves (content ''
  // renders time-only) — the backfill upserts the place into the same row
  const bannerId = getSystemMessageId(chatRoomId, meetingMetadata.meetingDate, profileUserId);
  await persistMessage(
    chatRoomId,
    buildSystemMeetingMessage({
      id: bannerId,
      content: formatSystemMessageLocation(meetingMetadata),
      meetingDate: meetingMetadata.meetingDate,
      meetingUtcOffset: meetingMetadata.meetingUtcOffset,
    })
  );
  if (onRecord) await onRecord(record);
  if (!meetingMetadata.locationData) {
    backfillNewMeetingLocation({
      chatRoomId,
      profileUserId,
      record,
      bannerId,
      locationDataRef,
      locationPromiseRef,
      updateConnectionMetadata,
      onRecord,
    });
  }
};

/**
 * The guest side of a guest↔host connect files its meeting row from the HOST's record
 * — the host decides the meeting and its key; the guest's record takes the host's
 * values when the record arrives (mergeIncomingMetadata). Runs only when a meeting key
 * the guest's record didn't have arrives (a first meeting, or a re-meet), so re-reads
 * and later place updates cost nothing. Profile-to-profile rooms file their rows in the
 * exchange itself (repeat connects above), never here.
 *
 * @param {string} chatRoomId
 * @param {string} myId - this person's id (their own meeting rows)
 * @param {Object|null} previousRecord - their record before the host's arrived
 * @param {Object|null} hostRecord - the host's record as received
 */
export const fileMeetingFromHostRecord = (chatRoomId, myId, previousRecord, hostRecord) => {
  const key = hostRecord?.meetingKey;
  if (!key || hostRecord.exchangeType === 'pxp') return;
  if (previousRecord?.meetingKey === key) return;
  upsertConnectionView(chatRoomId, myId, { metadata: { ...previousRecord, ...hostRecord } });
};
